import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AddressSearch } from "@/features/addresses/AddressSearch";
import { checkAddress, type AddressCandidate, type AddressCheckResult } from "@/features/addresses/addresses.api";
import { getLocation, listLocations, locationsKeys, type LocationListItem } from "@/features/locations/locations.api";
import { formatAddress } from "@/features/locations/labels";
import { requirementLabelKey } from "@/features/locations/requirements";
import { describeOpeningHours, formatMinutes, isoWeekday, openSegments } from "@/features/locations/openingHours";
import { useHoursWords } from "@/features/locations/OpeningHoursEditor";
import { formatActivityDate } from "./orderLabels";
import { focusNextAfterChoice, nextFocusable } from "@/lib/focusNext";

// Where an activity loads or unloads: a known location — found by name,
// code, place, postal code or any other way it is written — or, when the address
// book does not have it, the address as the customer gave it.

export interface GivenAddress {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

export type ActivityPlace = { kind: "location"; location: LocationListItem } | { kind: "address"; address: GivenAddress };

const EMPTY_ADDRESS: GivenAddress = {
  name: "",
  street: "",
  houseNumber: "",
  houseNumberAddition: "",
  postalCode: "",
  city: "",
  countryCode: "NL",
};

const LIST_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: "var(--z-overlay)",
  margin: "4px 0 0",
  padding: "4px",
  listStyle: "none",
  background: "var(--surface-raised)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-md)",
  maxHeight: "320px",
  overflowY: "auto",
};

function optionStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 8px",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    background: active ? "var(--surface-hover)" : undefined,
  };
}

/** Under a chosen place: the hours of that day, what the place asks, and the planner's instruction. */
export function ActivityInfo({ locationId, date }: { locationId: string; date: string }) {
  const { t } = useTranslation();
  const words = useHoursWords();
  const query = useQuery({ queryKey: locationsKeys.detail(locationId), queryFn: () => getLocation(locationId), staleTime: 60_000 });
  const location = query.data;
  if (!location) return null;

  const hours = location.openingHours;
  let hoursText: string;
  if (!date || hours.status !== "known") {
    hoursText = describeOpeningHours(hours, words);
  } else {
    const segments = openSegments(hours, isoWeekday(date));
    hoursText =
      segments.length === 0
        ? t("orders.dayClosed", { date: formatActivityDate(date) })
        : t("orders.dayOpen", {
            date: formatActivityDate(date),
            hours: segments.map(([from, to]) => `${formatMinutes(from)}–${formatMinutes(to)}`).join(", "),
          });
  }

  return (
    <div className="t-caption" style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px", alignItems: "center", marginTop: "6px" }}>
      <span className="t-muted">{hoursText}</span>
      {location.requirements.map((requirement) => {
        const key = requirementLabelKey(requirement);
        return (
          <span key={requirement} className="wl-chip">
            {key ? t(key as never) : requirement}
          </span>
        );
      })}
      {location.preNotification?.bookSlot && <span className="wl-chip wl-chip--navy">{t("locations.preBookSlot")}</span>}
    </div>
  );
}

/**
 * What the address lookup makes of an address as it was typed (docs/address-validation.md).
 * The order intake asked HERE for suggestions but never for a judgement, so a half or a
 * made-up address went through without a word (Camiel, 20-09-2026). It says so now — and
 * it never blocks: a judgement is a heads-up, and what the customer asked for stays what
 * the order says until someone takes the suggestion over.
 */
function AddressVerdict({ address, onTakeOver }: { address: GivenAddress; onTakeOver: (candidate: AddressCandidate) => void }) {
  const { t } = useTranslation();
  const [result, setResult] = useState<AddressCheckResult | null>(null);
  const latest = useRef(0);

  // Enough to ask about: a place and a country. Below that every answer would be "not
  // found", which says nothing about the address and everything about the typing.
  const askable = address.city.trim().length > 1 && address.countryCode.trim().length === 2;
  const key = JSON.stringify(address);

  useEffect(() => {
    if (!askable) {
      setResult(null);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      checkAddress({
        street: address.street.trim() || null,
        houseNumber: address.houseNumber.trim() || null,
        houseNumberAddition: address.houseNumberAddition.trim() || null,
        postalCode: address.postalCode.trim() || null,
        city: address.city.trim(),
        countryCode: address.countryCode.trim().toUpperCase(),
      })
        .then((answer) => {
          if (request === latest.current) setResult(answer);
        })
        .catch(() => {
          // The lookup being down is not the planner's problem: say nothing.
          if (request === latest.current) setResult(null);
        });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, askable]);

  if (!result) return null;

  const labels: Record<AddressCheckResult["verdict"], string> = {
    valid: t("orders.addressVerdictValid"),
    corrected: t("orders.addressVerdictCorrected"),
    partial: t("orders.addressVerdictPartial"),
    not_found: t("orders.addressVerdictNotFound"),
  };
  const good = result.verdict === "valid";
  const offer = result.suggestion && (result.differences.length > 0 || result.completions.length > 0);

  return (
    <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
      <span className="t-caption" style={{ color: good ? "var(--text-secondary)" : "var(--text-warning)", fontWeight: "var(--fw-medium)" }}>
        {labels[result.verdict]}
      </span>
      {offer && <span className="t-caption t-muted">{result.suggestion!.label}</span>}
      {offer && (
        <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onTakeOver(result.suggestion!)}>
          {t("orders.addressTakeOver")}
        </button>
      )}
      {!good && <span className="t-caption t-muted">{t("orders.addressVerdictHelp")}</span>}
    </div>
  );
}

export function LocationPicker({
  id,
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  id: string;
  value: ActivityPlace | null;
  onChange: (place: ActivityPlace | null) => void;
  disabled?: boolean;
  /** In a table the column head names the field, not a label above it. */
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [items, setItems] = useState<LocationListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const latest = useRef(0);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setItems([]);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      listLocations({ q, isActive: "true" })
        .then((page) => {
          if (request !== latest.current) return;
          setItems(page.data.slice(0, 8));
          setActive(-1);
        })
        .catch(() => {
          if (request === latest.current) setItems([]);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  if (value?.kind === "location") {
    return (
      <div
        className="wl-hstack"
        style={{ gap: "8px", alignItems: "flex-start", justifyContent: "space-between", minHeight: "var(--control-height-md)" }}
      >
        <div>
          <div className="t-body-sm" style={{ fontWeight: "var(--fw-medium)" }}>
            {value.location.name} <span className="t-mono t-caption t-muted">{value.location.code}</span>
          </div>
          <div className="t-caption t-muted">{formatAddress(value.location)}</div>
        </div>
        {!disabled && (
          <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(null)}>
            {t("orders.placeChange")}
          </button>
        )}
      </div>
    );
  }

  if (value?.kind === "address") {
    const address = value.address;
    const set = (key: keyof GivenAddress, next: string) => onChange({ kind: "address", address: { ...address, [key]: next } });
    const pick = (candidate: AddressCandidate) =>
      onChange({
        kind: "address",
        address: {
          ...address,
          street: candidate.street ?? "",
          houseNumber: candidate.houseNumber ?? "",
          houseNumberAddition: "",
          postalCode: candidate.postalCode ?? "",
          city: candidate.city ?? "",
          countryCode: candidate.countryCode ?? address.countryCode,
        },
      });
    const input = (key: keyof GivenAddress, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
      <div className="wl-field">
        <label className="wl-field__label" htmlFor={`${id}-${key}`}>
          {label}
        </label>
        <input
          id={`${id}-${key}`}
          className="wl-input"
          value={address[key]}
          disabled={disabled}
          onChange={(e) => set(key, e.target.value)}
          {...extra}
        />
      </div>
    );
    return (
      <div
        className="wl-vstack"
        style={{
          gap: "10px",
          alignItems: "stretch",
          padding: "10px 12px",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <div className="wl-hstack" style={{ justifyContent: "space-between" }}>
          <span className="t-caption" style={{ fontWeight: "var(--fw-semibold)" }}>
            {t("orders.givenAddressTitle")}
          </span>
          {!disabled && (
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(null)}>
              {t("orders.placeFromBook")}
            </button>
          )}
        </div>
        {input("name", t("orders.placeNameLabel"), { maxLength: 200 })}
        {!disabled && <AddressSearch id={`${id}-search`} onPick={pick} />}
        <div className="wl-grid-2" style={{ gap: "10px" }}>
          {input("street", t("locations.streetLabel"), { maxLength: 200 })}
          {input("houseNumber", t("locations.houseNumberLabel"), { maxLength: 20 })}
          {input("postalCode", t("locations.postalCodeLabel"), { maxLength: 20 })}
          {input("city", t("locations.cityLabel"), { maxLength: 100 })}
          {input("countryCode", t("locations.countryLabel"), { maxLength: 2, style: { textTransform: "uppercase" } })}
        </div>
        {!disabled && <AddressVerdict address={address} onTakeOver={pick} />}
      </div>
    );
  }

  const listId = `${id}-list`;
  const giveAddressIndex = items.length;
  const choose = (index: number) => {
    const item = items[index];
    setOpen(false);
    if (item) onChange({ kind: "location", location: item });
    else onChange({ kind: "address", address: { ...EMPTY_ADDRESS, name: text.trim() } });
  };

  return (
    <div ref={wrapper} style={{ position: "relative" }}>
      <input
        id={id}
        className="wl-input"
        type="search"
        role="combobox"
        aria-label={ariaLabel}
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        placeholder={t("orders.placeSearchPlaceholder")}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((index) => Math.min(index + 1, giveAddressIndex));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if ((e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) && active >= 0) {
            e.preventDefault();
            // Tab takes the highlighted row too, as in the customer picker (21-09-2026), and
            // goes on: to the next field after a location, into the first address field after
            // "give an address".
            const tab = e.key === "Tab";
            const location = active < items.length;
            const next = tab && location ? nextFocusable(wrapper.current) : null;
            choose(active);
            if (tab && location) focusNextAfterChoice(next);
            else if (tab) window.setTimeout(() => document.getElementById(`${id}-name`)?.focus(), 0);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && (
        <ul id={listId} role="listbox" style={LIST_STYLE}>
          {items.map((item, index) => (
            <li
              key={item.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(index);
              }}
              onMouseEnter={() => setActive(index)}
              style={optionStyle(index === active)}
            >
              <div className="t-body-sm">
                {item.name} <span className="t-mono t-caption t-muted">{item.code}</span>
              </div>
              <div className="t-caption t-muted">{formatAddress(item)}</div>
            </li>
          ))}
          {text.trim().length >= 2 && items.length === 0 && (
            <li className="t-caption t-muted" style={{ padding: "6px 8px" }}>
              {t("orders.placeNothingFound")}
            </li>
          )}
          <li
            id={`${listId}-${giveAddressIndex}`}
            role="option"
            aria-selected={active === giveAddressIndex}
            className="t-body-sm"
            onMouseDown={(e) => {
              e.preventDefault();
              choose(giveAddressIndex);
            }}
            onMouseEnter={() => setActive(giveAddressIndex)}
            style={{
              ...optionStyle(active === giveAddressIndex),
              borderTop: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            {t("orders.placeGiveAddress")}
          </li>
        </ul>
      )}
    </div>
  );
}
